import { escapeHtml, safeUrl } from '../ui.js';
import { buildRegister, desktopApp } from '../files-register.js';
import { sizeText } from '../drive-folders.js';
import { driveThumbnail } from '../google-api.js';
import { openFile } from './google-panels.js';
import { watchFileUse, markFileInUse, clearFileInUse, FILE_USE_STALE_MS } from '../db.js';
import { openOnDesktop, canOpenOnDesktop, openerSeen } from '../desktop-open.js';
import { auth } from '../firebase.js';

// A PROJECT'S FILES IN COLUMNS (Karan's "turn 8" — 8a project columns, 8b file
// preview, 8c preview slide-over; built 09/10/2026).
//
//   folders | the folder as a register | preview
//
// Folders come from Drive, in Drive's names; Track adds none. The middle
// column lists DOCUMENTS (files-register.js): one row per number and revision,
// every format together, older revisions greyed and struck through under the
// current one, files that do not follow the numbering as orange "rename" rows,
// other people's files under the subfolder they sit in. The preview shows
// Drive's thumbnail and the document's facts; on a narrower screen it slides
// in from the right instead (8c).
//
// Open in AutoCAD goes through Google Drive for desktop and the Track Opener
// helper (desktop-open.js). Opening a file that way marks it "in use" for the
// team (db.js fileUse) until "Done with it" — Drive itself cannot say who has a
// file open. The mark is advisory: it warns, it never blocks.
//
// Keys: ↑/↓ or j/k move, Space previews (the slide-over on a narrow screen,
// Track's viewer on a wide one), ↵ opens it in its app, ⌫ goes back, Esc
// closes the slide-over.
//
// House rule: every value from Drive or Firestore goes through escapeHtml(),
// every href through safeUrl(). Nothing here renames, moves or deletes a file.

const SORT_KEY = 'fc-sort';
const pad2 = (n) => String(n).padStart(2, '0');
const isSupersededPath = (path) => (path || []).some((p) => /^superseded$/i.test(String(p).trim()));
const thumbCache = new Map();

function when(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const now = new Date();
  const hm = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  if (d.toDateString() === now.toDateString()) return `Today ${hm}`;
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return `Yesterday ${hm}`;
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)}`;
}
function sinceText(ts) {
  const d = ts && typeof ts.toDate === 'function' ? ts.toDate() : null;
  if (!d) return 'just now';
  const now = new Date();
  const hm = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return d.toDateString() === now.toDateString() ? `since ${hm}` : `since ${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${hm}`;
}

/**
 * opts:
 *   session
 *   job        a loaded Files project: { project, folders, folderId, folderUrl }
 *   folderId   the folder to open on
 *   summary    the line under the title ("Slab inspection · … · 143 files, 68 match the convention")
 *   onBack()   back to the register
 *   onPhotos(fo)  7. Images opens the photo grid
 *   onOpenProject(id, number)  the project page, from the title
 */
export function renderProjectColumns(mount, opts) {
  const { session, job, summary = '', onBack = () => {}, onPhotos = null, onOpenProject = null } = opts;
  const p = job.project;
  const project = String(p.number);
  const folders = job.folders || [];
  const me = auth && auth.currentUser ? auth.currentUser.uid : null;
  let sort = 'number';
  try { sort = localStorage.getItem(SORT_KEY) === 'date' ? 'date' : 'number'; } catch (_) { /* private mode */ }
  const st = {
    folder: folders.find((f) => f.id === opts.folderId) || folders[0] || null,
    sel: -1,             // index into items
    slide: false,        // the 8c slide-over is open
    use: new Map(),      // Drive file id → in-use mark
    hint: null,          // { key, text } — after an Open in AutoCAD that may not have worked
  };
  let items = [];        // what the middle column lists, in order: { kind, key, row|file, folderPath }

  const clientLabel = p.clientName || p.title || '';
  mount.innerHTML = `
    <div class="fc-wrap">
      <div class="fc-head">
        <div class="fc-head-text">
          <button type="button" class="fc-back" id="fc-back">← Projects</button>
          <h2 class="fc-title">${onOpenProject && p.id ? `<button type="button" class="fc-title-btn" id="fc-proj"><span class="fc-num">${escapeHtml(project)}</span> ${escapeHtml(clientLabel)}</button>` : `<span class="fc-num">${escapeHtml(project)}</span> ${escapeHtml(clientLabel)}`}</h2>
          <p class="fc-sub">${escapeHtml(summary)}</p>
        </div>
        ${job.folderUrl || job.folderId ? `<a class="fc-drive" href="${escapeHtml(safeUrl(job.folderUrl || `https://drive.google.com/drive/folders/${encodeURIComponent(job.folderId)}`))}" target="_blank" rel="noopener">Open in Drive</a>` : ''}
      </div>
      <div class="fc" id="fc">
        <nav class="fc-folders" aria-label="Folders">
          <span class="fc-cap">Folders</span>
          <div class="fc-folder-list" id="fc-folder-list"></div>
          <p class="fc-foot">Folders come from Drive. Track adds nothing.</p>
        </nav>
        <section class="fc-list" aria-label="Files">
          <div class="fc-list-head"><span class="fc-cap" id="fc-list-cap"></span><button type="button" class="fc-sort" id="fc-sort" aria-label="Sort order"></button></div>
          <div class="fc-rows" id="fc-rows" role="listbox" tabindex="0" aria-label="Documents"></div>
          <div class="fc-keys" aria-hidden="true"><span><kbd>↵</kbd> Open</span><span><kbd>Space</kbd> Preview</span><span><kbd>j</kbd> <kbd>k</kbd> Move</span><span><kbd>⌫</kbd> Back</span></div>
        </section>
        <aside class="fc-pane" id="fc-pane" aria-label="Preview"></aside>
      </div>
      <div class="fc-scrim" id="fc-scrim" hidden></div>
      <aside class="fc-slide" id="fc-slide" aria-label="Preview" hidden></aside>
    </div>`;
  const root = mount.querySelector('.fc-wrap');
  const $ = (s) => root.querySelector(s);
  $('#fc-back').addEventListener('click', () => onBack());
  if ($('#fc-proj')) $('#fc-proj').addEventListener('click', () => onOpenProject(p.id, project));
  $('#fc-sort').addEventListener('click', () => {
    sort = sort === 'number' ? 'date' : 'number';
    try { localStorage.setItem(SORT_KEY, sort); } catch (_) { /* private mode */ }
    const key = items[st.sel] && items[st.sel].key;
    drawList(key);
  });
  $('#fc-scrim').addEventListener('click', () => setSlide(false));

  // Narrow: the preview column gives way to the slide-over (8c).
  const fc = $('#fc');
  const narrow = () => fc.classList.contains('is-narrow');
  const ro = new ResizeObserver(() => {
    const was = narrow();
    fc.classList.toggle('is-narrow', fc.clientWidth < 1040);
    if (was !== narrow()) { if (!narrow()) setSlide(false); drawPane(); }
  });
  ro.observe(fc);
  fc.classList.toggle('is-narrow', fc.clientWidth < 1040);

  // ── Folders ──────────────────────────────────────────────────────────────
  function drawFolders() {
    $('#fc-folder-list').innerHTML = folders.map((fo) => {
      const n = fo.files.filter((f) => !isSupersededPath(f.path)).length;
      return `<button type="button" class="fc-folder${st.folder && fo.id === st.folder.id ? ' is-on' : ''}" data-fo="${escapeHtml(fo.id)}" aria-current="${st.folder && fo.id === st.folder.id ? 'true' : 'false'}">
          <span class="fc-folder-name">${escapeHtml(fo.name)}</span><span class="fc-count">${escapeHtml(String(n))}</span></button>`;
    }).join('') || '<p class="fc-empty">No folders in this project on Drive.</p>';
    const on = root.querySelector('.fc-folder.is-on');
    if (on && window.matchMedia('(max-width: 760px)').matches) on.scrollIntoView({ block: 'nearest', inline: 'center' });
    root.querySelectorAll('[data-fo]').forEach((b) => b.addEventListener('click', () => {
      const fo = folders.find((x) => x.id === b.dataset.fo);
      if (!fo) return;
      if (fo.code === '07' && onPhotos) { stop(); onPhotos(fo); return; }
      st.folder = fo; st.sel = -1; st.hint = null;
      drawFolders(); drawList(); setSlide(false);
    }));
  }

  // ── The register ─────────────────────────────────────────────────────────
  function drawList(keepKey = null) {
    const fo = st.folder;
    $('#fc-list-cap').textContent = fo ? fo.name : '';
    $('#fc-sort').textContent = sort === 'date' ? 'by date' : 'by number';
    if (!fo) { $('#fc-rows').innerHTML = '<p class="fc-empty">Nothing here.</p>'; items = []; drawPane(); return; }
    const reg = buildRegister(fo, project, { sort });
    items = [
      ...reg.docs.map((row) => ({ kind: 'doc', key: row.key, row })),
      ...reg.fixes.map((x) => ({ kind: 'fix', key: x.key, row: x, file: x.file })),
      ...reg.others.flatMap((g) => g.files.map((f, i) => ({ kind: 'file', key: `f_${f.id}`, file: f, heading: i === 0 ? g.heading : null, group: g.heading }))),
    ];
    if (keepKey) st.sel = items.findIndex((x) => x.key === keepKey);
    if (st.sel >= items.length) st.sel = items.length - 1;
    const html = items.map((it, i) => {
      const sel = i === st.sel ? ' is-sel' : '';
      const use = useOf(it);
      const mark = use ? `<span class="fc-use${use.uid === me ? ' is-mine' : ''}" title="${escapeHtml(`In use: ${use.uid === me ? 'you' : use.by}${use.app ? `, in ${use.app}` : ''}`)}" aria-label="In use"></span>` : '<span class="fc-use-none"></span>';
      if (it.kind === 'doc') {
        const r = it.row;
        return `<div class="fc-row${sel}${r.superseded ? ' is-old' : ''}" data-i="${i}" role="option" aria-selected="${i === st.sel}">
            <span class="fc-no">${escapeHtml(r.short)}</span><span class="fc-t">${escapeHtml(r.title || r.files[0].name)}</span><span class="fc-rev">${escapeHtml(r.rev || '—')}</span>${mark}</div>`;
      }
      const head = it.kind === 'file' && it.heading != null && it.heading !== '' ? `<div class="fc-group">${escapeHtml(it.heading)}</div>` : (it.kind === 'file' && it.heading === '' && i > 0 ? '<div class="fc-group">Not numbered</div>' : '');
      if (it.kind === 'fix') {
        return `<div class="fc-row is-fix${sel}" data-i="${i}" role="option" aria-selected="${i === st.sel}" title="${escapeHtml(it.row.reason)}">
            <span class="fc-no fc-rename"><span class="fc-dot" aria-hidden="true"></span>rename</span><span class="fc-t">${escapeHtml(it.file.name)}</span><span class="fc-rev">—</span>${mark}</div>`;
      }
      return `${head}<div class="fc-row is-plain${sel}" data-i="${i}" role="option" aria-selected="${i === st.sel}">
          <span class="fc-no fc-ext">${escapeHtml(ext(it.file.name))}</span><span class="fc-t">${escapeHtml(it.file.name)}</span><span class="fc-rev"></span>${mark}</div>`;
    }).join('');
    $('#fc-rows').innerHTML = html || '<p class="fc-empty">This folder is empty.</p>';
    root.querySelectorAll('.fc-row[data-i]').forEach((r) => {
      r.addEventListener('click', () => select(Number(r.dataset.i), { slide: narrow() }));
      r.addEventListener('dblclick', () => { select(Number(r.dataset.i)); primary(); });
    });
    drawPane();
  }
  const ext = (name) => ((String(name).match(/\.([A-Za-z0-9]{1,5})$/) || [, ''])[1] || '').toUpperCase();

  function select(i, { slide = false } = {}) {
    if (i < 0 || i >= items.length) return;
    st.sel = i; st.hint = null;
    root.querySelectorAll('.fc-row[data-i]').forEach((r) => {
      const on = Number(r.dataset.i) === i;
      r.classList.toggle('is-sel', on);
      r.setAttribute('aria-selected', String(on));
      if (on) r.scrollIntoView({ block: 'nearest' });
    });
    if (slide) setSlide(true); else drawPane();
  }

  // ── In use ───────────────────────────────────────────────────────────────
  const filesOf = (it) => (it.kind === 'doc' ? it.row.files : [it.file]);
  function useOf(it) {
    for (const f of filesOf(it)) {
      const u = st.use.get(f.id);
      if (u && !stale(u)) return u;
    }
    return null;
  }
  const stale = (u) => { const d = u.since && u.since.toDate ? u.since.toDate() : null; return d ? Date.now() - d.getTime() > FILE_USE_STALE_MS : false; };
  const unwatch = watchFileUse(project, (list) => {
    if (!root.isConnected) { stop(); return; }
    st.use = new Map(list.map((u) => [u.fileId, u]));
    const key = items[st.sel] && items[st.sel].key;
    drawList(key);
  });

  // ── The preview ──────────────────────────────────────────────────────────
  function setSlide(on) {
    st.slide = Boolean(on) && narrow() && st.sel >= 0;
    $('#fc-slide').hidden = !st.slide;
    $('#fc-scrim').hidden = !st.slide;
    root.classList.toggle('is-sliding', st.slide);
    drawPane();
  }

  function mainFiles(it) {
    if (it.kind !== 'doc') return { open: it.file, view: it.file, cad: desktopApp(it.file.name) ? it.file : null };
    const r = it.row;
    const cad = r.cad || r.files.find((f) => desktopApp(f.name)) || null;
    return { cad, view: r.pdf || r.files[0], open: cad || r.pdf || r.files[0] };
  }

  function paneHtml(it) {
    if (!it) {
      return `<div class="fc-pane-empty"><p>Select a file to preview it here.</p><p>Space previews · ↵ opens it in its app.</p></div>`;
    }
    const { cad, view } = mainFiles(it);
    const app = cad ? desktopApp(cad.name) : null;
    const desk = app && canOpenOnDesktop();
    const use = useOf(it);
    const r = it.kind === 'doc' ? it.row : null;
    const number = r ? `${r.number}${r.rev ? `_${r.rev}` : ''}` : it.file.name;
    const title = r ? (r.title || '') : (it.kind === 'fix' ? 'Not numbered to the house rule' : '');
    const below = [st.folder.name, ...((r ? r.path : it.file.path) || [])];
    const crumbs = ['Projects', `${project} ${clientLabel}`.trim(), ...below];
    const modified = r ? r.modifiedTime : it.file.modifiedTime;
    const facts = [];
    if (r) facts.push(['Revision', `${r.rev || 'First issue'}${r.superseded ? ` · superseded${r.current && r.current !== r.rev ? ` by ${r.current}` : ''}` : ''}`]);
    if (desk || use) {
      const who = use ? `${use.uid === me ? 'You' : escapeHtml(use.by || 'Someone')}${use.app ? `, in ${escapeHtml(use.app)}` : ''}, ${escapeHtml(sinceText(use.since))}` : 'No one';
      const act = use ? (use.uid === me ? '<button type="button" class="fc-mini" data-act="done">Done with it</button>' : '')
        : (cad && canOpenOnDesktop() ? '<button type="button" class="fc-mini" data-act="mark">Mark in use</button>' : '');
      facts.push(['In use', `${use ? `<span class="fc-use${use.uid === me ? ' is-mine' : ''}" aria-hidden="true"></span>` : ''}${who}${act ? ` ${act}` : ''}`, true]);
    }
    facts.push(['Modified', escapeHtml(when(modified))]);
    if (r) facts.push(['Previous', escapeHtml(r.prevRev || 'none')]);
    if (r && r.formats.length) facts.push(['Formats', escapeHtml(r.formats.join(' · '))]);
    if (r && r.cad && st.folder.code === '05') facts.push(['Issues as', 'PDF, never the CAD file']);
    if (it.kind === 'fix') {
      facts.push(['Why', escapeHtml(it.row.reason)]);
      if (it.row.suggestion) facts.push(['Rename to', `<span class="fc-mono">${escapeHtml(it.row.suggestion)}</span>`, true]);
    }
    if (!r) facts.push(['Size', escapeHtml(sizeText(it.file.size))]);
    const thumbFor = view || it.file;
    const primaryLabel = desk ? `Open in ${app}` : 'Open';
    const viewLink = desk && view && view.id !== cad.id ? `<button type="button" class="fc-link" data-act="view">View ${escapeHtml(ext(view.name))} in Track</button>`
      : (desk ? '<button type="button" class="fc-link" data-act="view">View in Track</button>' : '');
    const hint = st.hint && st.hint.key === it.key ? `<div class="fc-hint" role="status">${st.hint.html}</div>` : '';
    const driveHref = (cad || view || it.file).webViewLink;
    return `
      ${st.slide ? '<div class="fc-slide-top"><span class="fc-cap">Preview</span><button type="button" class="fc-esc" data-act="close" aria-label="Close preview">esc</button></div>' : ''}
      <div class="fc-thumb" data-thumb="${escapeHtml(thumbFor.id)}"><span>${escapeHtml(thumbFor.name)} · preview from Drive</span></div>
      <div class="fc-id">
        <span class="fc-idno">${escapeHtml(number)}</span>
        ${title ? `<span class="fc-idtitle">${escapeHtml(title)}</span>` : ''}
        <span class="fc-crumbs">${crumbs.map((c, k) => (k === 0 ? `<button type="button" class="fc-crumb" data-act="back">${escapeHtml(c)}</button>` : `<span>${escapeHtml(c)}</span>`)).join('<span class="fc-crumb-sep" aria-hidden="true">▸</span>')}</span>
      </div>
      <dl class="fc-facts">${facts.map(([k, v, html]) => `<div class="fc-fact"><dt>${escapeHtml(k)}</dt><dd>${html ? v : v}</dd></div>`).join('')}</dl>
      <div class="fc-actions">
        <button type="button" class="fc-btn fc-btn-primary" data-act="open">${escapeHtml(primaryLabel)}</button>
        <div class="fc-btn-pair">
          ${driveHref ? `<a class="fc-btn" href="${escapeHtml(safeUrl(driveHref))}" target="_blank" rel="noopener">Open in Drive</a>` : ''}
          ${driveHref ? '<button type="button" class="fc-btn" data-act="copy">Copy link</button>' : ''}
        </div>
        ${viewLink}
        ${hint}
      </div>`;
  }

  function drawPane() {
    const it = items[st.sel] || null;
    const host = st.slide ? $('#fc-slide') : $('#fc-pane');
    if (st.slide) $('#fc-pane').innerHTML = paneHtml(null);
    else $('#fc-slide').innerHTML = '';
    host.innerHTML = paneHtml(st.slide || !narrow() ? it : null);
    host.querySelectorAll('[data-act]').forEach((b) => b.addEventListener('click', () => act(b.dataset.act, b)));
    const th = host.querySelector('[data-thumb]');
    if (th && (st.slide || !narrow()) && it) loadThumb(th);
  }

  function loadThumb(el) {
    const id = el.dataset.thumb;
    const put = (url) => {
      if (!url || !el.isConnected) return;
      const img = new Image();
      img.alt = '';
      img.referrerPolicy = 'no-referrer';
      img.onload = () => { if (el.isConnected) { el.innerHTML = ''; el.appendChild(img); el.classList.add('has-img'); } };
      img.src = safeUrl(url.replace(/=s\d+$/, '=s800'));
    };
    if (thumbCache.has(id)) { put(thumbCache.get(id)); return; }
    driveThumbnail(id).then((u) => { thumbCache.set(id, u); put(u); }).catch(() => thumbCache.set(id, null));
  }

  // ── Actions ──────────────────────────────────────────────────────────────
  function viewInTrack(f) {
    const reviewer = { reference: f.name, title: f.name, status: isSupersededPath(f.path) ? 'Superseded' : 'Current',
      user: (session && (session.displayName || session.email)) || '', email: (session && session.email) || null, projectNo: project };
    const list = items.map((x) => mainFiles(x).view).filter(Boolean);
    openFile(f, { reviewer, reviewerFor: (g) => ({ ...reviewer, reference: g.name, title: g.name }), siblings: list });
  }

  async function openDesktop(it, f, app) {
    const use = useOf(it);
    if (use && use.uid !== me && !window.confirm(`${use.by || 'Someone'} has this open${use.app ? ` in ${use.app}` : ''} ${sinceText(use.since)}.\n\nOpen it anyway? If you both save, Drive keeps two copies.`)) return;
    const below = [st.folder.name, ...((it.kind === 'doc' ? (f.path || it.row.path) : f.path) || [])];
    let res;
    try { res = await openOnDesktop(f, { folderId: job.folderId, below }); }
    catch (e) { st.hint = { key: it.key, html: escapeHtml((e && e.message) || 'Drive could not be read.') }; drawPane(); return; }
    if (res === 'opened') {
      if (!use) markFileInUse({ fileId: f.id, project, name: f.name, app }).catch(() => {});
      return;
    }
    st.hint = { key: it.key, html: `${openerSeen() ? 'Nothing opened?' : 'Nothing opened? This computer needs Track Opener, once:'}
      <a href="/downloads/TrackOpener-windows.zip" download>Windows</a> · <a href="/downloads/TrackOpener-mac.zip" download>Mac</a> — and Google Drive for desktop signed in as you. Help → Files says how.
      ${!use ? '<button type="button" class="fc-mini" data-act="mark">It opened — mark in use</button>' : ''}` };
    drawPane();
  }

  function primary() {
    const it = items[st.sel];
    if (!it) return;
    const { cad, view } = mainFiles(it);
    const app = cad ? desktopApp(cad.name) : null;
    if (app && canOpenOnDesktop()) openDesktop(it, cad, app);
    else viewInTrack(view || it.file);
  }

  function act(what, el) {
    const it = items[st.sel];
    if (what === 'close') { setSlide(false); return; }
    if (what === 'back') { stop(); onBack(); return; }
    if (!it) return;
    const { cad, view } = mainFiles(it);
    if (what === 'open') primary();
    else if (what === 'view') viewInTrack(view || it.file);
    else if (what === 'copy') {
      const href = (cad || view || it.file).webViewLink;
      navigator.clipboard.writeText(href).then(() => { el.textContent = 'Copied'; setTimeout(() => { if (el.isConnected) el.textContent = 'Copy link'; }, 1600); },
        () => { el.textContent = 'Could not copy'; });
    } else if (what === 'done') {
      const u = useOf(it);
      if (u) clearFileInUse(u.fileId).catch(() => {});
    } else if (what === 'mark') {
      const f = cad || it.file;
      st.hint = null;
      markFileInUse({ fileId: f.id, project, name: f.name, app: desktopApp(f.name) || '' }).catch(() => {});
    }
  }

  // ── Keys ─────────────────────────────────────────────────────────────────
  function onKey(e) {
    if (!root.isConnected) { stop(); return; }
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
    // A viewer or a dialog in front keeps its own keys (a hidden one does not count).
    if ([...document.querySelectorAll('.overlay, .dr-viewer, [role="dialog"]')].some((el) => el.getClientRects().length > 0)) return;
    const k = e.key;
    if (k === 'j' || k === 'ArrowDown') { e.preventDefault(); select(Math.min(items.length - 1, st.sel + 1), { slide: st.slide }); }
    else if (k === 'k' || k === 'ArrowUp') { e.preventDefault(); select(Math.max(0, st.sel - 1), { slide: st.slide }); }
    else if (k === ' ') {
      if (t && t.tagName === 'BUTTON' || t && t.tagName === 'A') return;
      e.preventDefault();
      if (st.sel < 0) { select(0, { slide: narrow() }); return; }
      if (narrow()) setSlide(!st.slide);
      else { const it = items[st.sel]; const { view } = mainFiles(it); viewInTrack(view || it.file); }
    } else if (k === 'Enter') {
      if (t && (t.tagName === 'BUTTON' || t.tagName === 'A')) return;
      e.preventDefault(); primary();
    } else if (k === 'Escape' && st.slide) { e.preventDefault(); setSlide(false); }
    else if (k === 'Backspace') { e.preventDefault(); stop(); onBack(); }
  }
  document.addEventListener('keydown', onKey);
  let stopped = false;
  function stop() {
    if (stopped) return;
    stopped = true;
    document.removeEventListener('keydown', onKey);
    ro.disconnect();
    try { unwatch(); } catch (_) { /* already gone */ }
  }

  drawFolders();
  drawList();
  return { stop };
}

